import React, { useCallback, useEffect, useState } from 'react';
import {
  createOpenvasConnector,
  getOpenvasConnector,
  updateOpenvasConnector,
} from '../../../services/connector.service';
import ConnectorPasswordField from './ConnectorPasswordField';
import ScannerAssignmentPicker from './ScannerAssignmentPicker';
import {
  emptyDisplayValue,
  getOpenvasConnectorInitialState,
  isPermissionDeniedError,
  mapAssignedSitesFromConnector,
  sanitizeOpenvasConnectorField,
} from './connectorFormUtils';

function draftFromConnector(data) {
  return {
    name: String(data?.openvas_name || data?.scanner_name || '').trim(),
    vanityName: String(data?.openvas_vanity_name || data?.scanner_vanity_name || '').trim(),
    host: String(data?.openvas_host || data?.scanner_host || '').trim(),
    port: data?.openvas_port != null && data?.openvas_port !== ''
      ? String(data.openvas_port)
      : data?.scanner_port != null && data?.scanner_port !== ''
        ? String(data.scanner_port)
        : '',
    gvmUser: String(data?.openvas_user || '').trim(),
    gvmPassword: '',
    sshUser: String(data?.ssh_user || '').trim(),
    sshPassword: '',
    perimeterScanner: !!data?.perimeter_scanner,
    enabled: !!data?.openvas_enabled,
  };
}

function ConnectorTextField({ id, label, hint, value, onChange, readOnly, placeholder, inputMode }) {
  return (
    <div className="connector-openvas-field">
      <label className="ghost-exposure-sys-field-label" htmlFor={id}>
        {label}
      </label>
      {hint ? <span className="connector-openvas-field-sub">{hint}</span> : null}
      {readOnly ? (
        <p className="connector-openvas-readonly" id={id}>{emptyDisplayValue(value)}</p>
      ) : (
        <input
          id={id}
          type="text"
          inputMode={inputMode}
          className="ghost-exposure-sys-input"
          placeholder={placeholder}
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

export default function NetworkScannerSettingsForm({
  connectorId = null,
  canManage = false,
  onClose,
  onSaved,
}) {
  const isExisting = Boolean(connectorId);
  const [draft, setDraft] = useState(getOpenvasConnectorInitialState);
  const [snapshot, setSnapshot] = useState(getOpenvasConnectorInitialState);
  const [assignedIds, setAssignedIds] = useState([]);
  const [assignedSnapshot, setAssignedSnapshot] = useState([]);
  const [knownAssignments, setKnownAssignments] = useState([]);
  const [isEditing, setIsEditing] = useState(true);
  const [loading, setLoading] = useState(isExisting);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showGvmPassword, setShowGvmPassword] = useState(false);
  const [showSshPassword, setShowSshPassword] = useState(false);

  useEffect(() => {
    if (!connectorId) {
      const initial = getOpenvasConnectorInitialState();
      setDraft(initial);
      setSnapshot(initial);
      setAssignedIds([]);
      setAssignedSnapshot([]);
      setKnownAssignments([]);
      setIsEditing(true);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError('');
    getOpenvasConnector(connectorId)
      .then((data) => {
        if (cancelled) return;
        const nextDraft = draftFromConnector(data);
        const assigned = mapAssignedSitesFromConnector(data);
        const ids = assigned.map((item) => item.id);
        setDraft(nextDraft);
        setSnapshot(nextDraft);
        setAssignedIds(ids);
        setAssignedSnapshot(ids);
        setKnownAssignments(assigned);
        setIsEditing(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.message || 'Could not load connector.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [connectorId]);

  const updateDraft = useCallback((patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const startEdit = () => {
    if (!canManage) return;
    setSaveError('');
    setIsEditing(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEditing || saving) return;
    if (!canManage) {
      setSaveError('You have read-only connector access. Save is not allowed for your role.');
      return;
    }
    const name = draft.name.trim();
    const host = draft.host.trim();
    const port = draft.port.trim();
    if (!name) {
      setSaveError('Name is required.');
      return;
    }
    if (!host) {
      setSaveError('Host is required.');
      return;
    }
    if (!port) {
      setSaveError('Port is required.');
      return;
    }
    const parsedPort = parseInt(port, 10);
    if (!Number.isFinite(parsedPort)) {
      setSaveError('Port must be a number.');
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        openvas_name: name,
        openvas_vanity_name: draft.vanityName.trim() || name,
        openvas_host: host,
        openvas_port: parsedPort,
        openvas_user: draft.gvmUser.trim(),
        ssh_user: draft.sshUser.trim(),
        openvas_enabled: draft.enabled,
        perimeter_scanner: draft.perimeterScanner,
        site_uuids: assignedIds,
      };
      if (draft.gvmPassword) payload.openvas_password = draft.gvmPassword;
      if (draft.sshPassword) payload.ssh_password = draft.sshPassword;
      if (connectorId) await updateOpenvasConnector(connectorId, payload);
      else await createOpenvasConnector(payload);
      onSaved?.();
      onClose?.();
    } catch (err) {
      if (isPermissionDeniedError(err)) {
        setSaveError('You have read-only connector access. Save is not allowed for your role.');
      } else {
        setSaveError(err?.message || 'Failed to save connector.');
      }
    } finally {
      setSaving(false);
    }
  };

  const readOnly = !isEditing;

  return (
    <>
      <div className="new-scan-modal-header connector-openvas-header-row connector-openvas-modal-header">
        <div className="connector-zap-header-title-block">
          <h2 id="add-connector-openvas-title" className="ghost-cve-section-title new-scan-modal-title">
            Network scanner settings
          </h2>
          <div
            className={`connector-zap-header-trailing${
              draft.enabled ? ' connector-zap-header-trailing--on' : ' connector-zap-header-trailing--off'
            }`}
            data-tour="settings-connectors-modal-enable"
          >
            <span className="connector-zap-enable-label">
              {draft.enabled ? 'Connector Enabled' : 'Connector Disabled'}
            </span>
            <button
              type="button"
              role="switch"
              className={`connector-zap-enable-switch${draft.enabled ? ' is-on' : ' is-off'}`}
              aria-checked={draft.enabled}
              aria-label={
                draft.enabled
                  ? 'Connector enabled. Click to turn off.'
                  : 'Connector disabled. Click to turn on.'
              }
              disabled={readOnly || !canManage}
              onClick={() => updateDraft({ enabled: !draft.enabled })}
            >
              <span className="connector-zap-enable-switch-thumb" aria-hidden />
            </button>
          </div>
        </div>
        <button type="button" className="new-scan-modal-close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      {loading ? (
        <div className="connector-openvas-form">
          <p className="connector-assignment-empty" role="status">Loading connector…</p>
        </div>
      ) : loadError ? (
        <div className="connector-openvas-form">
          <p className="profile-settings-message profile-settings-error" role="alert">{loadError}</p>
        </div>
      ) : (
        <form className="connector-openvas-form" id="connector-openvas-form" onSubmit={handleSubmit} noValidate>
          <div className="connector-openvas-form-scroll" data-tour="settings-connectors-modal-fields">
            <section className="settings-modal-card" aria-labelledby="openvas-connection-heading">
              <div className="settings-modal-card-head">
                <h3 id="openvas-connection-heading" className="settings-modal-card-title">
                  Connection details
                </h3>
                <p className="settings-modal-card-hint">
                  Identify this OpenVAS connector and authentication details.
                </p>
              </div>
              <div className="settings-modal-card-body">
                <ConnectorTextField
                  id="openvas-name"
                  label="Name"
                  value={draft.name}
                  onChange={(value) => updateDraft({ name: sanitizeOpenvasConnectorField(value) })}
                  readOnly={readOnly}
                  placeholder="Connector name"
                />
                <ConnectorTextField
                  id="openvas-vanity-name"
                  label="Vanity name"
                  value={draft.vanityName}
                  onChange={(value) => updateDraft({ vanityName: sanitizeOpenvasConnectorField(value) })}
                  readOnly={readOnly}
                  placeholder="Display name"
                />
                <ConnectorTextField
                  id="openvas-host"
                  label="Host"
                  value={draft.host}
                  onChange={(value) => updateDraft({ host: sanitizeOpenvasConnectorField(value) })}
                  readOnly={readOnly}
                  placeholder="Host"
                />
                <ConnectorTextField
                  id="openvas-port"
                  label="Port"
                  value={draft.port}
                  onChange={(value) =>
                    updateDraft({ port: sanitizeOpenvasConnectorField(value).replace(/[^\d]/g, '') })
                  }
                  readOnly={readOnly}
                  placeholder="Port"
                  inputMode="numeric"
                />
                <ConnectorTextField
                  id="openvas-gvm-user"
                  label="GVM user"
                  value={draft.gvmUser}
                  onChange={(value) => updateDraft({ gvmUser: sanitizeOpenvasConnectorField(value) })}
                  readOnly={readOnly}
                  placeholder="User name"
                />
                {readOnly ? (
                  <div className="connector-openvas-field">
                    <span className="ghost-exposure-sys-field-label">GVM password</span>
                    <p className="connector-openvas-readonly">
                      {isExisting ? 'Stored on the connector. Edit to replace.' : '—'}
                    </p>
                  </div>
                ) : (
                  <ConnectorPasswordField
                    id="openvas-gvm-pass"
                    label="GVM password"
                    value={draft.gvmPassword}
                    onChange={(value) => updateDraft({ gvmPassword: sanitizeOpenvasConnectorField(value) })}
                    revealed={showGvmPassword}
                    onToggleReveal={() => setShowGvmPassword((v) => !v)}
                    placeholder={isExisting ? 'Enter a new password to replace the stored value' : 'Password'}
                  />
                )}
                <ConnectorTextField
                  id="openvas-ssh-user"
                  label="SSH user"
                  value={draft.sshUser}
                  onChange={(value) => updateDraft({ sshUser: sanitizeOpenvasConnectorField(value) })}
                  readOnly={readOnly}
                  placeholder="User name"
                />
                {readOnly ? (
                  <div className="connector-openvas-field">
                    <span className="ghost-exposure-sys-field-label">SSH password</span>
                    <p className="connector-openvas-readonly">
                      {isExisting ? 'Stored on the connector. Edit to replace.' : '—'}
                    </p>
                  </div>
                ) : (
                  <ConnectorPasswordField
                    id="openvas-ssh-pass"
                    label="SSH password"
                    value={draft.sshPassword}
                    onChange={(value) => updateDraft({ sshPassword: sanitizeOpenvasConnectorField(value) })}
                    revealed={showSshPassword}
                    onToggleReveal={() => setShowSshPassword((v) => !v)}
                    placeholder={isExisting ? 'Enter a new password to replace the stored value' : 'Password'}
                  />
                )}
              </div>
            </section>

            <section className="settings-modal-card" aria-labelledby="openvas-perimeter-heading">
              <div className="settings-modal-card-head">
                <h3 id="openvas-perimeter-heading" className="settings-modal-card-title">
                  Perimeter scanner
                </h3>
                <p className="settings-modal-card-hint">
                  Control whether this connector can run perimeter scans.
                </p>
              </div>
              <div className="settings-modal-card-body">
                <label className="ghost-exposure-sys-check connector-openvas-perimeter-check">
                  <input
                    type="checkbox"
                    className="ghost-exposure-sys-check-input"
                    checked={draft.perimeterScanner}
                    disabled={readOnly}
                    onChange={(e) => updateDraft({ perimeterScanner: e.target.checked })}
                  />
                  <span>Enable perimeter scanner</span>
                </label>
              </div>
            </section>

            <section className="settings-modal-card connector-openvas-assignment" aria-labelledby="openvas-assignment-heading">
              <div className="settings-modal-card-head">
                <h3 id="openvas-assignment-heading" className="settings-modal-card-title">
                  Scanner assignment
                </h3>
                <p className="settings-modal-card-hint">
                  Selected organizations and sites are scanned by this connector.
                </p>
              </div>
              <div className="settings-modal-card-body">
                <ScannerAssignmentPicker
                  selectedIds={assignedIds}
                  knownAssignments={knownAssignments}
                  perimeterScanner={draft.perimeterScanner}
                  readOnly={readOnly}
                  onChange={setAssignedIds}
                />
              </div>
            </section>
          </div>

          <div className="connector-openvas-form-actions" data-tour="settings-connectors-modal-save">
            {saveError ? (
              <p className="profile-settings-message profile-settings-error" role="alert">
                {saveError}
              </p>
            ) : null}
            {readOnly ? (
              <button
                type="button"
                className="ghost-exposure-sys-btn ghost-exposure-sys-btn--primary"
                onClick={startEdit}
                disabled={!canManage}
              >
                Edit
              </button>
            ) : (
              <button
                type="submit"
                className="ghost-exposure-sys-btn ghost-exposure-sys-btn--primary"
                disabled={saving}
              >
                {connectorId ? 'Update' : 'Save'}
              </button>
            )}
          </div>
        </form>
      )}
    </>
  );
}
